// @ts-nocheck
import React, { useState, useMemo } from 'react';
import { X, User, Phone, Mail, Calendar, Building2, Users, Briefcase, Shield, AlertCircle, Check } from 'lucide-react';
import { createRecord } from '../../services/recordStorageService';
import { useQueryClient } from '@tanstack/react-query';
import { useUsers } from '../../services/queries/userQueries';

interface NewCyberSweepEnrollmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (newClient: Record<string, unknown>) => void;
}

// Picklist options based on Salesforce schema
const BULK_EMAIL_STATUS_OPTIONS = [
  'Single Opt-in',
  'Transactional Only'
];

const LEAD_SOURCE_OPTIONS = [
  'Advertisement',
  'Customer Event',
  'Employee Referral',
  'Google Adwords',
  'Partner',
  'Purchased List',
  'Tradeshow',
  'Webinar',
  'Website',
  'Social4M Event',
  'Client Referral',
  'External Referral',
  'Returning Client',
  'Other'
];

const SERVICE_MANAGEMENT_OPTIONS = [
  'Self',
  'Account Manager Managed'
];

const CYBER_SWEEP_STAGE_OPTIONS = [
  'Current Client',
  'Cancelled',
  'Complete'
];

// SSN validation regex (format: 000-00-0000)
const SSN_REGEX = /^\d{3}-\d{2}-\d{4}$/;

// Phone validation regex (US format: (000) 000-0000 or similar)
const PHONE_REGEX = /^\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}$/;

// Format SSN as user types
const formatSSN = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
};

// Format phone number as user types (US format: (000) 000-0000)
const formatPhoneNumber = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

// Get today's date in YYYY-MM-DD format
const getTodayDate = (): string => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

export const NewCyberSweepEnrollmentModal: React.FC<NewCyberSweepEnrollmentModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Fetch users for Account Owner dropdown
  const { data: users } = useUsers({});
  const sortedUsers = useMemo(() => {
    return users
      ?.filter(u => {
        const name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
        return name.length > 0;
      })
      ?.sort((a, b) => {
        const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim();
        const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim();
        return nameA.localeCompare(nameB);
      }) || [];
  }, [users]);

  // Form state
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    ssn: '',
    phone: '',
    email: '',

    // Account Information
    accountOwner: '',
    bulkEmailStatus: '',
    leadSource: '',

    // Referral Information
    referralCompany: '',
    referralPartnerContact: '',
    referralPersonContact: '',

    // Account Management
    serviceManagement: 'Self',
    accountManager: '',
    accountManagerCompany: '',
    dayToDayContact: '',

    // CyberSweep Specific
    cyberSweepStartDate: getTodayDate(),
    cyberSweepStage: 'Current Client'
  });

  // Validation errors
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  if (!isOpen) return null;

  const handleInputChange = (field: string, value: string) => {
    // Special handling for SSN formatting
    if (field === 'ssn') {
      value = formatSSN(value);
    }
    // Special handling for phone formatting
    if (field === 'phone') {
      value = formatPhoneNumber(value);
    }

    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First Name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last Name is required';
    }

    if (formData.ssn && !SSN_REGEX.test(formData.ssn)) {
      errors.ssn = 'SSN must be in format 000-00-0000';
    }

    if (formData.phone && !PHONE_REGEX.test(formData.phone)) {
      errors.phone = 'Please enter a valid phone number';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.cyberSweepStartDate) {
      errors.cyberSweepStartDate = 'CyberSweep Start Date is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Build Account record data for Gobodo. Current CyberSweep detail/update flows treat
      // enrolled clients as Account-shaped records, so create new enrollments on that object.
      const clientData: Record<string, unknown> = {
        // Name fields
        Name__c: `${formData.firstName} ${formData.lastName}`.trim(),
        FirstName__c: formData.firstName,
        LastName__c: formData.lastName,
        First_Name__c: formData.firstName,
        Last_Name__c: formData.lastName,

        // Personal Information
        SSN__c: formData.ssn || null,
        Phone__c: formData.phone || null,
        PersonEmail__c: formData.email || null,
        Email__c: formData.email || null,

        // Account Information
        OwnerId__c: formData.accountOwner || null,
        Bulk_Email_Status__c: formData.bulkEmailStatus || null,
        LeadSource__c: formData.leadSource || null,

        // Referral Information
        Referral_Company__c: formData.referralCompany || null,
        Referral_Partner_Contact__c: formData.referralPartnerContact || null,
        Referral_Person_Contact__c: formData.referralPersonContact || null,

        // Account Management
        Service_Management__c: formData.serviceManagement || null,
        Account_Manager__c: formData.accountManager || null,
        Account_Manager_Company__c: formData.accountManagerCompany || null,
        Day_to_Day_Contact__c: formData.dayToDayContact || null,

        // CyberSweep Specific - CRITICAL: This makes it a CyberSweep client
        // Set both field name variants (CyberSweep_Start_Date__c and Cyber_Sweep_Start_Date__c)
        CyberSweep_Start_Date__c: formData.cyberSweepStartDate,
        Cyber_Sweep_Start_Date__c: formData.cyberSweepStartDate,
        Cyber_Sweep_Status__c: 'Pending Enrollments',
        Cyber_Sweep_Stage__c: formData.cyberSweepStage || 'Current Client',

        // Also set camelCase variants for Gobodo field resolution
        cyberSweepStartDate: formData.cyberSweepStartDate,
        cyberSweepStatus: 'Pending Enrollments',
        cyberSweepStage: formData.cyberSweepStage || 'Current Client',
        custom__cyber_sweep_start_date: formData.cyberSweepStartDate,
        custom__cyber_sweep_status: 'Pending Enrollments',
        custom__cyber_sweep_stage: formData.cyberSweepStage || 'Current Client'
      };

      // Create Account record in Gobodo (matches current CyberSweep client detail/update flow)
      console.group('[NewCyberSweepEnrollment] Creating record');
      console.log('Object type: Account');
      console.log('Form data:', formData);
      console.log('Client data being sent:', clientData);
      console.log('CyberSweep_Start_Date__c value:', clientData.CyberSweep_Start_Date__c);

      const newRecord = await createRecord('Account', clientData);

      console.log('Create response:', newRecord);
      console.log('New record ID:', newRecord?.id);
      console.log('New record key:', (newRecord as any)?.key);
      console.groupEnd();

      // Invalidate queries to refresh the board
      console.log('[NewCyberSweepEnrollment] Invalidating queries: cybersweep/clients, cybersweep/cs_audits');
      queryClient.invalidateQueries({ queryKey: ['cybersweep', 'clients'] });
      queryClient.invalidateQueries({ queryKey: ['cybersweep', 'cs_audits'] });
      queryClient.invalidateQueries({ queryKey: ['cybersweep', 'board-column'] });

      setSuccess(true);

      // Call success callback
      if (onSuccess) {
        onSuccess(newRecord);
      }

      // Auto-close after success message
      setTimeout(() => {
        handleClose();
      }, 1500);

    } catch (err) {
      console.error('[NewCyberSweepEnrollment] Error creating enrollment record:', err);
      setError(err instanceof Error ? err.message : 'Failed to create enrollment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset form state
    setFormData({
      firstName: '',
      lastName: '',
      ssn: '',
      phone: '',
      email: '',
      accountOwner: '',
      bulkEmailStatus: '',
      leadSource: '',
      referralCompany: '',
      referralPartnerContact: '',
      referralPersonContact: '',
      serviceManagement: 'Self',
      accountManager: '',
      accountManagerCompany: '',
      dayToDayContact: '',
      cyberSweepStartDate: getTodayDate(),
      cyberSweepStage: 'Current Client'
    });
    setValidationErrors({});
    setError(null);
    setSuccess(false);
    onClose();
  };

  const renderInput = (
    field: string,
    label: string,
    type: string = 'text',
    required: boolean = false,
    placeholder: string = '',
    icon?: React.ReactNode
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        {icon && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            {icon}
          </div>
        )}
        <input
          type={type}
          value={formData[field as keyof typeof formData] || ''}
          onChange={(e) => handleInputChange(field, e.target.value)}
          className={`w-full ${icon ? 'pl-10' : 'pl-3'} pr-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
            validationErrors[field]
              ? 'border-red-500 dark:border-red-400'
              : 'border-gray-300 dark:border-gray-600'
          }`}
          placeholder={placeholder}
        />
      </div>
      {validationErrors[field] && (
        <p className="mt-1 text-sm text-red-500 dark:text-red-400">{validationErrors[field]}</p>
      )}
    </div>
  );

  const renderSelect = (
    field: string,
    label: string,
    options: string[],
    required: boolean = false,
    placeholder: string = 'Select...'
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        value={formData[field as keyof typeof formData] || ''}
        onChange={(e) => handleInputChange(field, e.target.value)}
        className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
          validationErrors[field]
            ? 'border-red-500 dark:border-red-400'
            : 'border-gray-300 dark:border-gray-600'
        }`}
      >
        <option value="">{placeholder}</option>
        {options.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
      {validationErrors[field] && (
        <p className="mt-1 text-sm text-red-500 dark:text-red-400">{validationErrors[field]}</p>
      )}
    </div>
  );

  const renderLookupField = (
    field: string,
    label: string,
    lookupType: 'User' | 'Account' | 'Contact',
    required: boolean = false
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type="text"
        value={formData[field as keyof typeof formData] || ''}
        onChange={(e) => handleInputChange(field, e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        placeholder={`Search ${lookupType}...`}
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-3xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Shield className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">New CyberSweep Enrollment</h2>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Success Message */}
          {success && (
            <div className="mx-6 mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-3">
              <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
              <span className="text-green-700 dark:text-green-300 font-medium">Enrollment created successfully!</span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mx-6 mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              <span className="text-red-700 dark:text-red-300">{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Personal Information Section */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                <User className="h-4 w-4 text-purple-500" />
                Personal Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {renderInput('firstName', 'First Name', 'text', true, 'Enter first name', <User className="h-4 w-4" />)}
                {renderInput('lastName', 'Last Name', 'text', true, 'Enter last name', <User className="h-4 w-4" />)}
                {renderInput('ssn', 'SSN', 'text', false, '000-00-0000', <Shield className="h-4 w-4" />)}
                {renderInput('phone', 'Phone', 'tel', false, '(555) 123-4567', <Phone className="h-4 w-4" />)}
                <div className="col-span-2">
                  {renderInput('email', 'Email', 'email', false, 'email@example.com', <Mail className="h-4 w-4" />)}
                </div>
              </div>
            </div>

            {/* Account Information Section */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-purple-500" />
                Account Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {/* Account Owner - User dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Account Owner
                  </label>
                  <select
                    value={formData.accountOwner || ''}
                    onChange={(e) => handleInputChange('accountOwner', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Select...</option>
                    {sortedUsers.map(user => (
                      <option key={user.id} value={user.id}>
                        {`${user.firstName || ''} ${user.lastName || ''}`.trim()}
                      </option>
                    ))}
                  </select>
                </div>
                {renderSelect('bulkEmailStatus', 'Bulk Email Status', BULK_EMAIL_STATUS_OPTIONS)}
                <div className="col-span-2">
                  {renderSelect('leadSource', 'Lead Source', LEAD_SOURCE_OPTIONS)}
                </div>
              </div>
            </div>

            {/* Referral Information Section */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                <Users className="h-4 w-4 text-purple-500" />
                Referral Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {renderLookupField('referralCompany', 'Referral Company', 'Account')}
                {renderLookupField('referralPartnerContact', 'Referral Partner Contact', 'Contact')}
                <div className="col-span-2">
                  {renderLookupField('referralPersonContact', 'Referral Person Contact', 'Account')}
                </div>
              </div>
            </div>

            {/* Account Management Section */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-purple-500" />
                Account Management
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {renderSelect('serviceManagement', 'Service Management', SERVICE_MANAGEMENT_OPTIONS)}
                {renderLookupField('accountManager', 'Account Manager', 'Contact')}
                {renderLookupField('accountManagerCompany', 'Account Manager Company', 'Account')}
                {renderLookupField('dayToDayContact', 'Day-to-Day Contact', 'Contact')}
              </div>
            </div>

            {/* CyberSweep Specific Section */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                <Calendar className="h-4 w-4 text-purple-500" />
                CyberSweep Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {renderInput('cyberSweepStartDate', 'CyberSweep Start Date', 'date', true)}
                {renderSelect('cyberSweepStage', 'Cyber Sweep Stage', CYBER_SWEEP_STAGE_OPTIONS)}
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting || success}
              className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Creating...
                </>
              ) : success ? (
                <>
                  <Check className="h-4 w-4" />
                  Created!
                </>
              ) : (
                'Create Enrollment'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewCyberSweepEnrollmentModal;
